// src/v2/leases/LeaseDrawerRentSchedule.jsx
//
// Task #9096 — drawer-only narrow rent schedule + remaining obligation tile.
// Rendered by LeaseDetailBody when variant="drawer". Uses the body's existing
// schedule (buildRentSchedule), display currency and FX rates. Evidence only:
// annual-only years never get a monthly split; missing years are never zero.
import { useState } from "react";
import { T } from "../../app/tokens.js";
import { fmtMoney } from "../../utils/format.js";
import { convertAmount } from "../../utils/fxConvert.js";
import { rowAmount, hasAbatementSubline, monthlyRowsForYear, remainingObligation, scheduleTotals, isEstimateRow, termCoverage } from "./leaseDrawerFinancials.js";

// Shared narrow grid: Year · $/SF · Net rent · Esc. Month rows reuse the
// first three tracks so Amount sits exactly under Net rent.
export const SCHEDULE_COLS = "76px 72px 124px 64px";
// Only Year/$/SF/Net rent are shared with the annual row. YTD needs a full
// money-width track, not the annual percentage-width Esc. track.
export const MONTH_COLS = "76px 72px 124px 124px minmax(100px,1fr)";
const eyebrow = { fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".08em", textTransform: "uppercase", color: T.mid };
const num = { fontVariantNumeric: "tabular-nums", textAlign: "right" };
const linkBtn = { border: 0, background: "transparent", padding: 0, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit" };

function fmtCents(n, ccy) {
  return fmtMoney(n, ccy, { decimals: 2 });
}

/**
 * Money in display currency when a rate exists, else native (labelled by its
 * own code). `{ cents: true }` keeps 2 decimals and sign (monthly cells);
 * default keeps the reader's whole-unit fmtMoney presentation.
 */
export function makeMoney(displayCcy, rates) {
  return (amount, from, { cents = false } = {}) => {
    if (amount == null || !Number.isFinite(Number(amount))) return "–";
    const src = from || "USD";
    const to = displayCcy || src;
    const f = (n, c) => (cents ? fmtCents(Math.round(n * 100) / 100, c) : fmtMoney(n, c));
    if (to === src) return f(Number(amount), src);
    const c = convertAmount(Number(amount), src, to, rates);
    return c != null && Number.isFinite(c) ? f(c, to) : f(Number(amount), src);
  };
}

/** Number in display currency (null when no rate): used for $/SF. */
function convertNum(amount, from, to, rates) {
  if (amount == null || !Number.isFinite(Number(amount))) return null;
  if (!to || from === to) return Number(amount);
  const c = convertAmount(Number(amount), from, to, rates);
  return c != null && Number.isFinite(c) ? c : null;
}

const STATUS = {
  paid: { label: "Paid", color: T.mid },
  now: { label: "Now", color: T.blue },
  scheduled: { label: "Scheduled", color: T.dark },
  missing: { label: "No payment recorded", color: T.amberText ?? T.ragAmber ?? T.dark, bg: T.warmBeige },
};

export function MonthDetail({ lease, row, money, today }) {
  const detail = monthlyRowsForYear(lease, row, today);
  const ccy = row.currency || lease?.currency || "USD";
  const box = { padding: "6px 0 10px", borderTop: `1px dashed ${T.border}` };
  if (detail.kind === "invalid") {
    return <div role="alert" data-testid={`month-detail-invalid-${row.year}`} style={{ ...box, fontSize: T.fsXs, color: T.dark }}>Monthly detail unavailable: {detail.issue}</div>;
  }
  if (detail.kind !== "monthly") {
    return (
      <div data-testid={`month-detail-annual-only-${row.year}`} style={{ ...box, fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>
        {detail.kind === "not_in_source"
          ? "This year is not in the source. No payments are shown."
          : "The source records an annual amount only for this year. No month-by-month split is recorded."}
      </div>
    );
  }
  return (
    <div data-testid={`month-detail-${row.year}`} style={box}>
      <div style={{ display: "grid", gridTemplateColumns: MONTH_COLS, ...eyebrow, padding: "2px 0 4px" }}>
        <span style={{ gridColumn: "1 / span 2" }}>Month</span><span style={num}>Amount</span><span style={num}>YTD</span><span style={{ paddingLeft: 12 }}>Status</span>
      </div>
      {[1, 2, 3, 4].map((q) => (
        <div key={q} data-testid={`month-quarter-${row.year}-q${q}`}>
          <div style={{ ...eyebrow, color: T.light, padding: "5px 0 1px" }}>Q{q}</div>
          {detail.months.filter((m) => m.quarter === q).map((m) => {
            const st = STATUS[m.status];
            return (
              <div key={m.month} data-testid={`month-row-${row.year}-${m.month}`}
                style={{ display: "grid", gridTemplateColumns: MONTH_COLS, alignItems: "center", fontSize: T.fsXs, padding: "3px 0", background: st.bg, borderRadius: 4 }}>
                <span style={{ gridColumn: "1 / span 2", color: T.dark }}>{m.label}</span>
                <span data-testid={`month-amount-${row.year}-${m.month}`} data-col="net" style={{ ...num, color: m.sourced ? T.dark : T.light }}>
                  {m.sourced ? money(m.amount, ccy, { cents: true }) : "–"}
                </span>
                <span style={{ ...num, color: T.mid, fontSize: T.fsXxs }}>{money(m.ytd, ccy, { cents: true })}</span>
                <span data-testid={`month-status-${row.year}-${m.month}`} style={{ paddingLeft: 12, color: st.color, fontWeight: T.fwBold, fontSize: T.fsXxs }}>{st.label}</span>
              </div>
            );
          })}
        </div>
      ))}
      <div data-testid={`month-total-${row.year}`} data-reconciles={detail.reconciles ? "true" : "false"}
        style={{ display: "grid", gridTemplateColumns: SCHEDULE_COLS, fontSize: T.fsXs, fontWeight: T.fwBold, paddingTop: 6, marginTop: 4, borderTop: `1px solid ${T.border}` }}>
        <span style={{ gridColumn: "1 / span 2" }}>Year total</span>
        <span style={num}>{money(detail.total, ccy, { cents: true })}</span>
        <span style={{ ...num, fontSize: T.fsXxs, color: detail.reconciles ? T.forestGreen ?? T.mid : T.ragRed }}>{detail.reconciles ? "= year" : "≠ year"}</span>
      </div>
      {hasAbatementSubline(row) && <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 4 }}>Abatement is already reflected in the sourced monthly amounts and is not subtracted again.</div>}
      <div data-testid={`month-status-legend-${row.year}`} style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 4 }}>
        Paid / Now / Scheduled follow the calendar date only. They do not confirm that a payment settled.
      </div>
      {detail.months.some((m) => m.status === "missing") && (
        <div data-testid={`month-missing-note-${row.year}`} style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 4 }}>
          "No payment recorded" means the source has no row for that month. It is missing evidence, not free rent.
        </div>
      )}
    </div>
  );
}

export function LeaseDrawerRentSchedule({ lease, schedule, displayCcy, rates, today = new Date() }) {
  const curY = today.getFullYear();
  const money = makeMoney(displayCcy, rates);
  const native = lease?.currency || "USD";
  const [mode, setMode] = useState("full");
  const [open, setOpen] = useState(() => new Set());
  const rows = (schedule || []).filter((r) => mode === "full" || Number(r.year) >= curY);
  const target = displayCcy || native;
  const totals = scheduleTotals(schedule, { toCcy: target, rates });
  const rem = remainingObligation(lease, schedule, today, { toCcy: target, rates });
  const allOpen = rows.length > 0 && rows.every((r) => open.has(r.year));
  const toggle = (y) => setOpen((s) => { const n = new Set(s); if (n.has(y)) n.delete(y); else n.add(y); return n; });
  const seg = (k, l) => (
    <button type="button" data-testid={`schedule-mode-${k}`} aria-pressed={mode === k} onClick={() => setMode(k)}
      style={{ border: 0, borderRadius: 6, height: 24, padding: "0 10px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit",
        background: mode === k ? T.card : "transparent", color: mode === k ? T.dark : T.mid, boxShadow: mode === k ? T.cardShadow : "none" }}>{l}</button>
  );
  return (
    <section data-testid="lease-drawer-rent-schedule" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: "11px 13px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
          <span aria-hidden="true" style={{ width: 3, height: 14, borderRadius: 2, background: T.blue }} />Rent schedule
        </span>
        <div style={{ display: "inline-flex", background: T.lightGray, borderRadius: 8, padding: 2 }}>{seg("full", "Full term")}{seg("remaining", "Remaining")}</div>
        <button type="button" data-testid="schedule-expand-all" style={linkBtn}
          onClick={() => setOpen(allOpen ? new Set() : new Set(rows.map((r) => r.year)))}>{allOpen ? "Collapse all" : "Expand all"}</button>
      </div>
      <div data-testid="schedule-scroll" style={{ marginTop: 10, overflowX: "auto", maxWidth: "100%" }}>
       <div style={{ minWidth: 420 }}>
        <div style={{ display: "grid", gridTemplateColumns: SCHEDULE_COLS, ...eyebrow, paddingBottom: 6, borderBottom: `1px solid ${T.border}` }}>
          <span>Year</span><span style={num}>/SF</span><span data-testid="schedule-head-net" data-col="net" style={num}>Net rent</span><span style={num}>Esc.</span>
        </div>
        {rows.length === 0 && <div data-testid="schedule-empty" style={{ padding: "12px 0", fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>No schedule rows in source{mode === "remaining" ? " from this year on" : ""}.</div>}
        {rows.map((r) => {
          const a = rowAmount(r); const isOpen = open.has(r.year); const y = Number(r.year);
          return (
            <div key={r.year} style={{ borderBottom: `1px solid ${T.hairline ?? T.border}` }}>
              <button type="button" data-testid={`schedule-year-${r.year}`} aria-expanded={isOpen} onClick={() => toggle(r.year)}
                style={{ display: "grid", gridTemplateColumns: SCHEDULE_COLS, border: 0, background: "transparent", padding: "7px 0", font: "inherit", fontSize: T.fsSm, cursor: "pointer", textAlign: "left", color: T.dark }}>
                <span style={{ fontWeight: y === curY ? T.fwBold : T.fwSemibold, color: y === curY ? T.blue : T.dark }}>
                  <span aria-hidden="true" style={{ display: "inline-block", width: 12, color: T.mid }}>{isOpen ? "▾" : "▸"}</span>{r.year}{r.isPartial ? "*" : ""}
                  {isEstimateRow(r) && <span data-testid={`schedule-estimate-${r.year}`} title="Fallback or projected figure, not an exact source amount"
                    style={{ marginLeft: 4, fontSize: T.fsXxs, color: T.amberText ?? T.mid, fontWeight: T.fwBold }}>est.</span>}
                </span>
                <span data-testid={`schedule-psf-${r.year}`} style={num}>{(() => {
                  const from = r.currency || native;
                  const v = convertNum(r.ratePsf, from, target, rates);
                  if (r.ratePsf == null) return "–";
                  return fmtMoney(v ?? r.ratePsf, v == null ? from : target, { decimals: 2, code: false });
                })()}</span>
                <span data-testid={`schedule-net-${r.year}`} data-col="net" style={{ ...num, fontWeight: T.fwBold, color: a == null ? T.light : T.dark, fontStyle: a == null ? "italic" : "normal" }}>
                  {a == null ? (r.basis === "unresolved" ? "Unresolved" : "Not in source") : money(a, r.currency || native)}
                </span>
                <span style={num}>{r.escPct != null ? `${Number(r.escPct).toFixed(1)}%` : "–"}</span>
              </button>
              {hasAbatementSubline(r) && (
                <div data-testid={`schedule-subline-${r.year}`} style={{ display: "grid", gridTemplateColumns: SCHEDULE_COLS, fontSize: T.fsXxs, color: T.mid, paddingBottom: 6 }}>
                  <span style={{ gridColumn: "1 / span 2", paddingLeft: 12 }}>Contractual / abated</span>
                  <span style={num}>{money(r.contractual, r.contractualCurrency || r.currency || native)}</span>
                  <span style={{ ...num, color: T.forestGreen ?? T.mid }}>−{money(r.abatement, r.currency || native)}</span>
                </div>
              )}
              {isOpen && <MonthDetail lease={lease} row={r} money={money} today={today} />}
            </div>
          );
        })}
        <div data-testid="schedule-total" style={{ display: "grid", gridTemplateColumns: SCHEDULE_COLS, fontSize: T.fsSm, fontWeight: T.fwBold, paddingTop: 8 }}>
          <span style={{ gridColumn: "1 / span 2" }}>{mode === "full" ? "Total sourced" : "Remaining"}</span>
          <span data-testid="schedule-total-value" style={num}>{mode === "full" ? fmtMoney(totals.total, target) : rem.amount != null ? fmtMoney(rem.amount, target) : "–"}</span>
        </div>
        {mode === "full" && (totals.missing > 0 || totals.estimates > 0 || totals.fxMissing.length > 0) && (
          <div data-testid="schedule-total-qualifier" style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 4 }}>
            {[totals.missing > 0 && `Excludes ${totals.missing} year${totals.missing > 1 ? "s" : ""} not in source (unknown, not zero)`,
              totals.estimates > 0 && `${totals.estimates} estimated year${totals.estimates > 1 ? "s" : ""} (est.) not counted as source`,
              totals.fxMissing.length > 0 && `no rate to ${target} for ${totals.fxMissing.join(", ")}`].filter(Boolean).join(" · ")}.
          </div>
        )}
        {mode === "remaining" && <div data-testid="schedule-remaining-note" style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>Today-forward, dated source only. {rem.note}</div>}
        {rows.some((r) => r.isPartial) && <div style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 2 }}>* Partial calendar year within the lease term.</div>}
       </div>
      </div>
    </section>
  );
}

export function RemainingObligationTile({ lease, schedule, displayCcy, rates, today = new Date() }) {
  const target = displayCcy || lease?.currency || "USD";
  const rem = remainingObligation(lease, schedule, today, { toCcy: target, rates });
  return (
    <div data-testid="headline-tile-remaining-obligation" data-complete={rem.complete ? "true" : "false"}
      style={{ background: T.card, border: `1px solid ${rem.complete ? T.border : T.ragAmber ?? T.border}`, borderRadius: 6, padding: "10px 14px", display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwSemibold, color: T.light, textTransform: "uppercase", letterSpacing: "0.05em" }}>Remaining obligation</div>
      <div style={{ fontSize: T.fsLg, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.1 }}>{rem.amount != null ? fmtMoney(rem.amount, target) : "–"}</div>
      <div style={{ fontSize: T.fsXxs, color: T.mid }}>{rem.note}</div>
    </div>
  );
}

/** Drawer-only lifetime coverage disclosure next to the headline strip. */
export function LifetimeCoverageNote({ lease, schedule }) {
  const c = termCoverage(lease, schedule);
  if (c.termYears == null) {
    return <div data-testid="lifetime-coverage-note" style={{ fontSize: T.fsXxs, color: T.mid }}>Total lifetime is the reader's computed term cost. No fixed term, so source coverage can't be stated.</div>;
  }
  const full = c.missingYears.length === 0 && c.estimateYears.length === 0;
  return (
    <div data-testid="lifetime-coverage-note" data-complete={full ? "true" : "false"} style={{ fontSize: T.fsXxs, color: full ? T.mid : T.amberText ?? T.dark }}>
      Total lifetime coverage: source schedule covers {c.sourced} of {c.termYears} term years ({c.from}–{c.to}).
      {c.missingYears.length > 0 && ` Not in source: ${c.missingYears.join(", ")}.`}
      {c.estimateYears.length > 0 && ` Estimated: ${c.estimateYears.join(", ")}.`}
      {!full && " The lifetime figure is not a fully sourced commitment."}
    </div>
  );
}

const BAR_H = 120;
/**
 * Drawer-only trajectory: every schedule year incl. not-in-source gaps and
 * estimates. Signed and zero known values keep their bar/marker and exact
 * hover value; Paid / Now / Scheduled are date-relative labels only.
 */
export function LeaseDrawerTrajectory({ schedule, nativeCcy, displayCcy, rates, today = new Date() }) {
  const curY = today.getFullYear();
  const target = displayCcy || nativeCcy || "USD";
  const money = makeMoney(target, rates);
  const bars = (schedule || []).map((r) => {
    const a = rowAmount(r);
    const v = a == null ? null : convertNum(a, r.currency || nativeCcy, target, rates);
    const y = Number(r.year);
    const when = y < curY ? "Paid" : y === curY ? "Now" : "Scheduled";
    const kind = a == null ? "gap" : isEstimateRow(r) ? "estimate" : "known";
    return { year: y, v, raw: a, ccy: r.currency || nativeCcy, when, kind };
  });
  const max = Math.max(1, ...bars.map((b) => (b.v != null ? Math.abs(b.v) : 0)));
  return (
    <section data-testid="lease-drawer-trajectory" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 6, padding: "11px 13px" }}>
      <div style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Cost trajectory</div>
      {bars.length === 0 ? <div style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic", marginTop: 8 }}>No schedule rows in source.</div> : (
        <div style={{ overflowX: "auto", marginTop: 10 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, minWidth: bars.length * 34, height: BAR_H + 34 }}>
            {bars.map((b) => {
              const h = b.v == null ? 0 : Math.max(b.v === 0 ? 0 : 3, Math.round((Math.abs(b.v) / max) * BAR_H));
              const label = b.kind === "gap" ? `${b.year}: not in source (unknown, not zero)`
                : `${b.year}: ${b.v != null ? money(b.v, target, { cents: true }) : `${money(b.raw, b.ccy, { cents: true })} (no rate)`} · ${b.when}${b.kind === "estimate" ? " · estimate, not source" : ""}`;
              const color = b.v != null && b.v < 0 ? T.ragRed ?? T.dark : b.when === "Now" ? T.blue : b.when === "Paid" ? T.mid : T.dark;
              return (
                <div key={b.year} data-testid={`trajectory-bar-${b.year}`} data-kind={b.kind} data-when={b.when} title={label} aria-label={label}
                  style={{ flex: "1 0 28px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                  {b.kind === "gap" ? (
                    <div style={{ width: "70%", height: 18, border: `1px dashed ${T.border}`, borderRadius: 3, fontSize: T.fsXxs, color: T.light, display: "grid", placeItems: "center" }}>n/s</div>
                  ) : b.v === 0 ? (
                    <div data-testid={`trajectory-zero-${b.year}`} style={{ width: "70%", height: 2, background: color }} />
                  ) : (
                    <div style={{ width: "70%", height: h, borderRadius: "3px 3px 0 0", opacity: b.kind === "estimate" ? 0.45 : 1,
                      background: b.kind === "estimate" ? `repeating-linear-gradient(45deg, ${color}, ${color} 3px, transparent 3px, transparent 6px)` : color }} />
                  )}
                  <div style={{ fontSize: T.fsXxs, color: b.year === curY ? T.blue : T.mid, marginTop: 4, fontWeight: b.year === curY ? T.fwBold : 400 }}>{String(b.year).slice(2)}{b.v != null && b.v < 0 ? "−" : ""}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div data-testid="trajectory-legend" style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 6 }}>
        Paid / Now / Scheduled are relative to today's date, not settlement. Dashed n/s = not in source (unknown, not zero). Hatched = estimate, not source. Red = negative payable.
      </div>
    </section>
  );
}
